'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect, useRef } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { getCurrentUser, logout, type User } from '../lib/api';
import TopBarSearch from './TopBarSearch';
import ThemeToggle from './ThemeToggle';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const closeTimerRef = useRef<number | NodeJS.Timeout | null>(null);
  const pathname = usePathname();
  const router = useRouter();

  // Only use transparent background on homepage
  const isHomepage = pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        setUser(null);
      } finally {
        setLoadingUser(false);
      }
    };

    loadUser();
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      setIsUserMenuOpen(false);
      router.push('/');
      router.refresh();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Full-screen pages (e.g. the 3D globe) render their own minimal chrome
  if (pathname === '/globe') {
    return null;
  }

  const navItems = [
    { href: '/', label: 'Home' },
    { href: '/globe', label: 'Globe' },
    { href: '/map', label: 'Interactive map' },
    { href: '/castles', label: 'Explore castles' },
    { href: '/blog', label: 'Blog' },
    { href: '/castle-guesser', label: 'Castle Guesser' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-[951] h-16 lg:h-20 transition-all duration-300 ${
        isScrolled || !isHomepage
          ? 'bg-white/95 backdrop-blur-lg shadow-lg border-b border-gray-100 dark:bg-gray-900/95 dark:border-gray-800'
          : 'bg-transparent'
      }`}
      role="banner"
    >
      <nav className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo (swap images depending on scroll/home state) */}
          <Link
            href="/"
            className="flex items-center space-x-2 group"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {/* Use white/red logo on green (homepage, not scrolled),
                use colored logo when scrolled or on inner pages. */}
            <Image
              src={
                isScrolled || !isHomepage
                  ? '/images/castlopedia-vertical-180x40.png'
                  : '/images/castlopedia-vertical-white-red-180x40.png'
              }
              alt="Castlopedia logo"
              width={180}
              height={40}
              className="h-8 lg:h-10 object-contain group-hover:scale-105 transition-transform duration-200"
              // eager, not priority: a preload here would compete with the page's LCP image
              loading="eager"
            />
            <span className="sr-only">Castlopedia</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-2">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`px-3 lg:px-4 py-2 rounded-lg text-sm lg:text-base font-medium transition-all duration-200 hover:scale-105 ${
                  isScrolled || !isHomepage
                    ? 'text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50'
                    : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Search & CTA Button & Auth */}
          <div className="hidden lg:flex items-center gap-2">
            <ThemeToggle isTransparent={isHomepage && !isScrolled} />
            <TopBarSearch isTransparent={isHomepage && !isScrolled} />
            {!loadingUser && (
              <>
                {user ? (
                  <div className="relative">
                    <button
                      onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                      aria-label={isUserMenuOpen ? 'Close user menu' : 'Open user menu'}
                      className={`flex items-center gap-2 px-2 lg:px-3 py-2 rounded-lg text-sm lg:text-base font-medium transition-all duration-200 hover:scale-105 ${
                        isScrolled || !isHomepage
                          ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                          : 'text-white/90 hover:bg-white/10 backdrop-blur-sm'
                      }`}
                    >
                      <span
                        className={`flex items-center justify-center w-10 h-10 rounded-full ${
                          isScrolled || !isHomepage
                            ? 'bg-forest-green-600 text-white'
                            : 'bg-white/20 text-white'
                        }`}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z M4 20c0-3.31 2.69-6 6-6h0"
                          />
                          <path
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M16 20v-1c0-2-2-3-4-3s-4 1-4 3v1"
                          />
                        </svg>
                        <span className="sr-only">{user.name}</span>
                      </span>
                      <svg
                        className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {isUserMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 dark:bg-gray-800 dark:border-gray-700">
                        <Link
                          href="/account"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          My account
                        </Link>
                        <Link
                          href="/account/favourites?tab=favourite"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          ❤️ Favourited
                        </Link>
                        <Link
                          href="/account/favourites?tab=visited"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          onClick={() => setIsUserMenuOpen(false)}
                        >
                          🧭 Has been
                        </Link>

                        {(user.role === 'ADMIN' || user.role === 'MODERATOR') && (
                          <Link
                            href="/admin"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            👑 Admin panel
                          </Link>
                        )}
                        <hr className="my-2" />
                        <button
                          onClick={handleLogout}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/50"
                        >
                          Log out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {/* Hoverable user icon with login/register menu for unauthenticated users */}
                    <div
                      className="relative"
                      onMouseEnter={() => {
                        // cancel any pending close
                        if (closeTimerRef.current) {
                          clearTimeout(closeTimerRef.current);
                          closeTimerRef.current = null;
                        }
                        setIsUserMenuOpen(true);
                      }}
                      onMouseLeave={() => {
                        // add small delay before closing to allow pointer to reach panel
                        closeTimerRef.current = setTimeout(() => setIsUserMenuOpen(false), 180);
                      }}
                    >
                      <Link
                        href="/login"
                        className={`flex items-center justify-center w-10 h-10 rounded-full cursor-pointer transition-colors duration-200 ${
                          isScrolled || !isHomepage
                            ? 'bg-forest-green-600 text-white'
                            : 'bg-white/20 text-white'
                        }`}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeWidth={1.5}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z M4 20c0-3.31 2.69-6 6-6h4c3.31 0 6 2.69 6 6"
                          />
                        </svg>
                        <span className="sr-only">Log in</span>
                      </Link>

                      {isUserMenuOpen && (
                        <div
                          className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50 dark:bg-gray-800 dark:border-gray-700"
                          onMouseEnter={() => {
                            if (closeTimerRef.current) {
                              clearTimeout(closeTimerRef.current);
                              closeTimerRef.current = null;
                            }
                            setIsUserMenuOpen(true);
                          }}
                          onMouseLeave={() => {
                            closeTimerRef.current = setTimeout(() => setIsUserMenuOpen(false), 180);
                          }}
                        >
                          <Link
                            href="/login"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            Log in
                          </Link>
                          <Link
                            href="/register"
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700"
                          >
                            Create account
                          </Link>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </>
            )}
            <Link
              href="/contribute"
              className={`ml-6 px-4 lg:px-6 py-2 lg:py-2.5 rounded-lg font-medium text-sm lg:text-base transition-all duration-200 hover:scale-105 ${
                isScrolled || !isHomepage
                  ? 'bg-gradient-to-r from-forest-green-600 to-forest-green-700 text-white hover:from-forest-green-700 hover:to-forest-green-800'
                  : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'
              }`}
            >
              Contribute
            </Link>
          </div>

          {/* Mobile Theme Toggle */}
          <div className="lg:hidden">
            <ThemeToggle isTransparent={isHomepage && !isScrolled} />
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg transition-colors duration-200"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <div className="w-6 h-6 flex flex-col justify-center items-center relative">
              <span
                className={`block w-5 h-0.5 transition-all duration-300 absolute ${
                  isScrolled || !isHomepage ? 'bg-gray-900 dark:bg-white' : 'bg-white'
                } ${isMobileMenuOpen ? 'rotate-45' : '-translate-y-1.5'}`}
              />
              <span
                className={`block w-5 h-0.5 transition-all duration-300 absolute ${
                  isScrolled || !isHomepage ? 'bg-gray-900 dark:bg-white' : 'bg-white'
                } ${isMobileMenuOpen ? 'opacity-0' : ''}`}
              />
              <span
                className={`block w-5 h-0.5 transition-all duration-300 absolute ${
                  isScrolled || !isHomepage ? 'bg-gray-900 dark:bg-white' : 'bg-white'
                } ${isMobileMenuOpen ? '-rotate-45' : 'translate-y-1.5'}`}
              />
            </div>
          </button>
        </div>

        {/* Mobile Menu (CSS transitions, no framer-motion) */}
        {isMobileMenuOpen && (
          <div className="lg:hidden">
            <div className="py-4 space-y-2 bg-white/95 backdrop-blur-lg rounded-lg mt-2 border border-gray-100 shadow-lg transition-all duration-300 dark:bg-gray-900/95 dark:border-gray-700">
              {/* Mobile Search */}
              <div className="px-2 pb-2">
                <TopBarSearch isTransparent={false} />
              </div>

              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className="block px-4 py-3 text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 transition-colors duration-200 rounded-lg mx-2 dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {!loadingUser && (
                <>
                  {user ? (
                    <>
                      <div className="px-2 pt-2 border-t border-gray-100 mt-2 dark:border-gray-700">
                        <div className="px-4 py-2 text-sm text-gray-500 flex items-center gap-3 dark:text-gray-400">
                          <span className="flex items-center justify-center w-8 h-8 rounded-full bg-forest-green-600 text-white">
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                            >
                              <path
                                strokeWidth={1.5}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z M4 20c0-3.31 2.69-6 6-6h0"
                              />
                            </svg>
                          </span>
                          <strong className="truncate">{user.name}</strong>
                        </div>
                        <Link
                          href="/account"
                          className="block px-4 py-3 text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 transition-colors duration-200 rounded-lg dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          My account
                        </Link>
                        <Link
                          href="/account/favourites?tab=favourite"
                          className="block px-4 py-3 text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 transition-colors duration-200 rounded-lg dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          ❤️ Favourited
                        </Link>
                        <Link
                          href="/account/favourites?tab=visited"
                          className="block px-4 py-3 text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 transition-colors duration-200 rounded-lg dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          🧭 Has been
                        </Link>

                        {(user.role === 'ADMIN' || user.role === 'MODERATOR') && (
                          <Link
                            href="/admin"
                            className="block px-4 py-3 text-gray-700 hover:text-forest-green-600 hover:bg-forest-green-50 transition-colors duration-200 rounded-lg dark:text-gray-200 dark:hover:text-forest-green-400 dark:hover:bg-forest-green-900/50"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            👑 Admin panel
                          </Link>
                        )}
                        <button
                          onClick={() => {
                            handleLogout();
                            setIsMobileMenuOpen(false);
                          }}
                          className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 transition-colors duration-200 rounded-lg dark:text-red-400 dark:hover:bg-red-900/50"
                        >
                          Log out
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="px-2 pt-2 border-t border-gray-100 mt-2 space-y-2 dark:border-gray-700">
                        <Link
                          href="/login"
                          className="block w-full px-4 py-3 text-gray-700 text-center rounded-lg hover:bg-gray-100 transition-colors duration-200 dark:text-gray-200 dark:hover:bg-gray-800"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Log in
                        </Link>
                        <Link
                          href="/register"
                          className="block w-full px-4 py-3 bg-forest-green-600 text-white text-center rounded-lg hover:bg-forest-green-700 transition-colors duration-200"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Sign up
                        </Link>
                      </div>
                    </>
                  )}
                </>
              )}

              <div className="px-2 pt-2">
                <Link
                  href="/contribute"
                  className="block w-full px-4 py-3 bg-gradient-to-r from-forest-green-600 to-forest-green-700 text-white text-center rounded-lg hover:from-forest-green-700 hover:to-forest-green-800 transition-colors duration-200"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Contribute
                </Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
